import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPinned, Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getTripStatus, type InviteEntry, type TripSummary, type User } from "@travelix/core";

import { useUserDoc } from "@/hooks/use-user";
import { useAuth } from "@/lib/auth-provider";
import { getDemoState } from "@/lib/demo/demo-store";
import { demoOwnerFromUser, toDemoSummary } from "@/lib/demo/demo-trip";
import {
  isOnboardingDone,
  startTour,
  useTourState,
} from "@/lib/onboarding/tour-state";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { NavHeader } from "@/components/nav-header";
import { TripCard } from "@/components/trip-card";
import { InlineAd } from "@/components/inline-ad";
import { FAB } from "@/components/fab";
import { EmptyState } from "@/components/empty-state";
import { InviteCard } from "@/components/invite-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Overline } from "@/components/ui/overline";
import { Segmented } from "@/components/ui/segmented";
import { Input } from "@/components/ui/input";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { cn } from "@/lib/utils";

/**
 * Persisted filter/sort keys — kept in localStorage so a user's setting
 * survives navigation to a trip and back, and across sessions.
 */
type StatusFilter = "all" | "upcoming" | "active" | "past";
type SortKey = "start-desc" | "start-asc" | "name";

export default function DashboardRoute() {
  const { data: userDoc, isLoading, isError, refetch } = useUserDoc();

  const invites = userDoc?.pendingInvites ?? [];
  const trips = userDoc?.trips ?? [];
  const hasContent = invites.length > 0 || trips.length > 0;
  const tour = useTourState();
  useAutoStartTour(userDoc);

  return (
    // Bottom padding reserves space for the FAB (96 px) and the global bottom
    // tab bar (via --bottom-tabs-h, which resolves to 0 when unmounted). The
    // ad slot lives inline at the top of the list and does not need bottom
    // reservation.
    <main
      className="relative min-h-full bg-bg pb-[calc(96px+var(--bottom-tabs-h,0px))]"
    >
      <NavHeader
        title="Your trips"
        display
        showThemeToggle
        maxWidthClassName="max-w-6xl xl:max-w-7xl"
      />

      <PullToRefresh onRefresh={() => refetch()}>
      {tour.active ? <SampleTripSection /> : null}
      {isLoading ? (
        <LoadingList />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : !hasContent ? (
        <EmptyDashboard />
      ) : (
        <div className="mx-auto max-w-6xl xl:max-w-7xl flex flex-col">
          <div className="px-5 pt-4 flex flex-col gap-6">
            {invites.length > 0 ? <InvitesSection invites={invites} /> : null}
            {trips.length > 0 ? (
              <TripsSection trips={trips} showAd={!tour.active} />
            ) : null}
          </div>
        </div>
      )}
      </PullToRefresh>

      {/* Positioning layer matches the grid's own max-width so the FAB
          lines up with the content's right edge instead of the true
          viewport edge on wide (desktop) screens — same trick BottomTabs
          uses for its centered inner row, just anchored instead of centered. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 mx-auto h-0 max-w-6xl xl:max-w-7xl">
        <FAB label="Create trip" asChild pulse={!hasContent} className="absolute pointer-events-auto">
          <Link to="/trip/create">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </Link>
        </FAB>
      </div>
    </main>
  );
}

// ─── Onboarding ───────────────────────────────────────────────
/**
 * First dashboard visit on this device → start the tutorial, for every
 * account (new or old) until it's finished or skipped. Waits for the user
 * doc so the tour opens on a loaded dashboard. Replay lives in Profile.
 */
function useAutoStartTour(userDoc: User | undefined) {
  const { user } = useAuth();
  const { active } = useTourState();
  const loaded = userDoc !== undefined;

  useEffect(() => {
    if (!user || !loaded || active) return;
    if (isOnboardingDone(user.uid)) return;
    startTour(demoOwnerFromUser(user), "auto");
  }, [user, loaded, active]);
}

/** The in-memory sample trip, shown only while the tutorial runs. */
function SampleTripSection() {
  const demo = getDemoState();
  if (!demo) return null;
  return (
    <section aria-label="Sample trip" className="mx-auto max-w-6xl xl:max-w-7xl px-5 pt-4">
      <Overline className="mb-2 px-1">Sample trip · not saved</Overline>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div data-tour="demo-trip-card">
          <TripCard trip={toDemoSummary(demo.trip)} />
        </div>
      </div>
    </section>
  );
}

// ─── Pending invites section ──────────────────────────────────
function InvitesSection({ invites }: { invites: InviteEntry[] }) {
  return (
    <section aria-label="Trip invitations">
      <Overline className="mb-2 px-1">Invited · {invites.length}</Overline>
      <div className="flex flex-col gap-3">
        {invites.map((inv) => (
          <InviteCard key={`${inv.tripId}-${inv.invitedBy}`} invite={inv} />
        ))}
      </div>
    </section>
  );
}

// ─── Trip list with filter / sort / search ────────────────────
// The ad slot is a row in this list (InlineAd) — after the second trip, or
// after the last one on shorter lists. See docs/product-brief.md § "Ad
// placement rules".
const AD_AFTER_INDEX = 1;

function TripsSection({ trips, showAd }: { trips: TripSummary[]; showAd: boolean }) {
  const [filter, setFilter] = useLocalStorageState<StatusFilter>(
    "dashboard.filter",
    "all",
  );
  const [sort, setSort] = useLocalStorageState<SortKey>(
    "dashboard.sort",
    "start-desc",
  );
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const counts = useMemo(() => {
    const c = { all: trips.length, upcoming: 0, active: 0, past: 0 };
    for (const t of trips) c[getTripStatus(t)] += 1;
    return c;
  }, [trips]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = trips;
    if (filter !== "all") list = list.filter((t) => getTripStatus(t) === filter);
    if (q) {
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.city ?? "").toLowerCase().includes(q),
      );
    }
    // Sort a copy — the incoming array is read from cache.
    const sorted = [...list];
    if (sort === "start-desc") {
      sorted.sort((a, b) => b.startDate.toMillis() - a.startDate.toMillis());
    } else if (sort === "start-asc") {
      sorted.sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());
    } else if (sort === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [trips, filter, sort, search]);

  return (
    <section aria-label="Your trips">
      <div className="mb-3 flex items-center justify-between gap-3 px-1">
        <Overline>Trips · {counts.all}</Overline>
        <button
          type="button"
          aria-label={searchOpen ? "Close search" : "Search trips"}
          aria-expanded={searchOpen}
          onClick={() => {
            setSearchOpen((v) => !v);
            if (searchOpen) setSearch("");
          }}
          className="flex h-8 w-8 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
        >
          {searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {searchOpen ? (
          <motion.div
            key="search"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <Input
              autoFocus
              placeholder="Search by name or city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-3"
              aria-label="Search trips"
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Segmented<StatusFilter>
          value={filter}
          onChange={setFilter}
          ariaLabel="Filter trips by status"
          className="min-w-0 max-w-md"
          options={[
            { value: "all", label: `All · ${counts.all}` },
            { value: "upcoming", label: `Upcoming · ${counts.upcoming}` },
            { value: "active", label: `Active · ${counts.active}` },
            { value: "past", label: `Past · ${counts.past}` },
          ]}
        />
        <SortMenu sort={sort} onChange={setSort} />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-4 py-8 text-center">
          <p className="m-0 text-[14px] text-ink-muted">
            No trips match {search ? `"${search}"` : "that filter"} .
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((trip, i) => (
            <Fragment key={trip.id}>
              <li>
                <TripCard trip={trip} />
              </li>
              {showAd && i === Math.min(AD_AFTER_INDEX, filtered.length - 1) ? (
                <li className="md:col-span-2 xl:col-span-3">
                  <InlineAd slot="dashboard" maxHeight={250} />
                </li>
              ) : null}
            </Fragment>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Compact popover with three sort choices. Native <select> underneath for
 *  a11y and keyboard support — styled with the same pill treatment as the
 *  segmented control so the row feels cohesive. */
function SortMenu({ sort, onChange }: { sort: SortKey; onChange: (s: SortKey) => void }) {
  return (
    <label
      className={cn(
        "inline-flex items-center gap-2 rounded-full bg-bg-sunken px-3 py-2 text-[13px] font-semibold text-ink",
        "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary",
      )}
    >
      <span className="text-ink-subtle">Sort</span>
      <select
        aria-label="Sort trips"
        value={sort}
        onChange={(e) => onChange(e.target.value as SortKey)}
        className="bg-transparent text-ink outline-none"
      >
        <option value="start-desc">Newest first</option>
        <option value="start-asc">Oldest first</option>
        <option value="name">Name (A → Z)</option>
      </select>
    </label>
  );
}

// ─── States ──────────────────────────────────────────────────
function LoadingList() {
  return (
    <div className="mx-auto max-w-6xl xl:max-w-7xl px-5 pt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" aria-busy>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="aspect-[5/3] w-full rounded-lg" />
      ))}
    </div>
  );
}

function EmptyDashboard() {
  return (
    <EmptyState
      icon={MapPinned}
      title="No trips yet"
      body="Start with a name, some dates, and a rough destination — or wait for a friend to invite you."
      cta={
        <Button asChild size="lg">
          <Link to="/trip/create">Plan your first trip</Link>
        </Button>
      }
    />
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      icon={MapPinned}
      title="Couldn't load your trips"
      body="Might be a flaky connection. Try again in a second."
      cta={
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}
